import { ref, watch, computed } from 'vue'
import { defineStore } from 'pinia'

const STORAGE_KEY = 'macumba:search'

export const useSearchStore = defineStore('search', () => {
  // Try to load initial state from localStorage
  const loadInitialState = () => {
    if (typeof window === 'undefined') return { travelers: { adults: 1, children: 0, pets: 0 }, dateRange: { startDate: null, endDate: null } }
    
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Convert ISO strings back to Date objects if they exist
        if (parsed.dateRange) {
          if (parsed.dateRange.startDate) parsed.dateRange.startDate = new Date(parsed.dateRange.startDate)
          if (parsed.dateRange.endDate) parsed.dateRange.endDate = new Date(parsed.dateRange.endDate)
        }
        return parsed
      }
    } catch (e) {
      console.error('[SearchStore] Failed to load persisted search state', e)
    }
    
    return { travelers: { adults: 1, children: 0, pets: 0 }, dateRange: { startDate: null, endDate: null } }
  }

  const initialState = loadInitialState()
  const travelers = ref(initialState.travelers)
  const dateRange = ref(initialState.dateRange)

  // Computed properties for validation
  const hasTravelers = computed(() => {
    return (travelers.value.adults || 0) + (travelers.value.children || 0) > 0
  })

  const hasDates = computed(() => {
    return !!(dateRange.value?.startDate && dateRange.value?.endDate)
  })

  const isComplete = computed(() => hasTravelers.value && hasDates.value)

  // Actions
  const setTravelers = (data) => {
    travelers.value = { ...travelers.value, ...data }
  }

  const setDateRange = (range) => {
    dateRange.value = range
  }

  const clearDates = () => {
    dateRange.value = { startDate: null, endDate: null }
  }

  // Persist changes to localStorage
  watch([travelers, dateRange], () => {
    if (typeof window === 'undefined') return
    
    try {
      const data = {
        travelers: travelers.value,
        dateRange: dateRange.value
      }
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (e) {
      console.error('[SearchStore] Failed to persist search state', e)
    }
  }, { deep: true })

  return {
    travelers,
    dateRange,
    hasTravelers,
    hasDates,
    isComplete,
    setTravelers,
    setDateRange,
    clearDates
  }
})
